import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle, AlertCircle, FileText, Pill, DollarSign, Pen } from 'lucide-react';

const ConsentDialog = ({ 
  open, 
  onOpenChange, 
  patientName, 
  services = [], 
  medications = [], 
  onConsent 
}) => {
  const sigCanvas = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // Calculate totals
  const servicesTotal = services.reduce((sum, s) => sum + (s.total || s.quantity * s.unit_price || 0), 0);
  const medicationsTotal = medications.reduce((sum, m) => sum + (m.total || m.quantity * m.unit_price || 0), 0);
  const grandTotal = servicesTotal + medicationsTotal;

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setHasSignature(false);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  const handleSubmit = () => {
    if (!hasSignature || !agreed) return;
    
    const signatureData = sigCanvas.current.toDataURL('image/png');
    const consentData = {
      signature: signatureData,
      timestamp: new Date().toISOString(),
      services_acknowledged: services.map(s => ({ name: s.service_name, quantity: s.quantity, total: s.total })),
      medications_acknowledged: medications.map(m => ({ name: m.medication_name, quantity: m.quantity, total: m.total })),
      total_acknowledged: grandTotal
    };
    
    onConsent(consentData);
    onOpenChange(false);
    
    // Reset state
    setHasSignature(false);
    setAgreed(false);
    sigCanvas.current?.clear();
  };

  const handleClose = () => {
    setHasSignature(false);
    setAgreed(false);
    sigCanvas.current?.clear();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="pb-2 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-blue-600" />
            Treatment Consent Form
          </DialogTitle>
        </DialogHeader>
        
        <DialogBody className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Patient Info */}
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Patient:</span> {patientName}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Date: {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Services Summary */}
          {services.length > 0 && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="bg-emerald-50 px-3 py-2 border-b flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold text-emerald-800 text-sm">Services Provided</span>
              </div>
              <div className="divide-y">
                {services.map((service, idx) => (
                  <div key={idx} className="px-3 py-2 flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{service.service_name}</p>
                      <p className="text-xs text-gray-500">Qty: {service.quantity}</p>
                    </div>
                    <span className="font-semibold text-gray-900">${(service.total || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div className="px-3 py-2 bg-emerald-50 flex justify-between items-center">
                  <span className="font-semibold text-emerald-800 text-sm">Services Subtotal</span>
                  <span className="font-bold text-emerald-800">${servicesTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Medications Summary */}
          {medications.length > 0 && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="bg-amber-50 px-3 py-2 border-b flex items-center gap-2">
                <Pill className="w-4 h-4 text-amber-600" />
                <span className="font-semibold text-amber-800 text-sm">Medications Dispensed</span>
              </div>
              <div className="divide-y">
                {medications.map((med, idx) => (
                  <div key={idx} className="px-3 py-2 flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{med.medication_name}</p>
                      <p className="text-xs text-gray-500">Qty: {med.quantity}</p>
                    </div>
                    <span className="font-semibold text-gray-900">${(med.total || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div className="px-3 py-2 bg-amber-50 flex justify-between items-center">
                  <span className="font-semibold text-amber-800 text-sm">Medications Subtotal</span>
                  <span className="font-bold text-amber-800">${medicationsTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Grand Total */}
          <div className="bg-slate-800 text-white p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                <span className="font-semibold">Total Amount Due</span>
              </div>
              <span className="text-2xl font-bold">${grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Consent Agreement */}
          <div className="bg-gray-50 p-3 rounded-lg border">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I, <strong>{patientName}</strong>, acknowledge that I have reviewed and understand 
                the services provided and medications dispensed as listed above. I agree to pay 
                the total amount of <strong>${grandTotal.toFixed(2)}</strong>.
              </span>
            </label>
          </div>

          {/* Signature Pad */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-3 py-2 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pen className="w-4 h-4 text-gray-600" />
                <span className="font-semibold text-gray-700 text-sm">Patient Signature</span>
              </div>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={clearSignature}
                className="text-xs h-7"
              >
                Clear
              </Button>
            </div>
            <div className="bg-white p-2">
              <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  canvasProps={{
                    width: 400,
                    height: 150,
                    className: 'w-full rounded-lg'
                  }}
                  onEnd={handleSignatureEnd}
                />
              </div>
              {!hasSignature && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Please sign above using your finger or stylus
                </p>
              )}
              {hasSignature && (
                <p className="text-xs text-emerald-600 text-center mt-2 flex items-center justify-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Signature captured
                </p>
              )}
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!hasSignature || !agreed}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {!agreed ? 'Please agree to terms' : !hasSignature ? 'Please sign above' : 'Confirm & Submit for Billing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentDialog;
